import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button, Card, CardBody, CardHeader } from '@platform/ui';

/**
 * The last thing between a thrown render error and a blank page (SR-54, rules §35.4).
 *
 * Until this existed, no `ErrorBoundary`, route `errorComponent` or `componentDidCatch`
 * existed anywhere in the three SPAs: one bad render — a list envelope where an array
 * was expected, say — replaced the whole booking funnel with white. React unmounts the
 * entire tree when nothing catches, so the failure is total and silent.
 *
 * Two of them are installed, because they catch different things:
 *
 * - **`AppErrorBoundary`** wraps `RouterProvider` in `main.tsx`, so an error thrown by a
 *   provider, the shell or the router itself still lands on a page a person can read.
 * - **`RouteErrorFallback`** is the router's `defaultErrorComponent`, so an error inside
 *   one screen keeps the navigation, the top bar and the impersonation banner: the
 *   operator can walk away from the broken screen rather than reloading the app.
 *
 * ## It never renders the error
 *
 * `error.message` from a *render* is a JavaScript failure, not one of the API's written
 * sentences (API.md §1.6), and the two must not be shown the same way: a thrown message
 * can carry a field name, a row id or a fragment of whatever data tripped it, and
 * `SECURITY.md` §4.4's rule is that only the messages the server wrote for display are
 * displayed. The exception goes to Sentry, scrubbed by `beforeSend` (`lib/sentry.ts`),
 * and the screen says one fixed sentence.
 */

const TITLE = 'This page could not be displayed';
const BODY =
  'Something went wrong on our side. Nothing you entered has been sent anywhere. Please ' +
  'try again, or call your clinic if it keeps happening.';

interface ErrorPanelProps {
  /** Re-renders the subtree. The router hands one in; the root boundary reloads. */
  onRetry: () => void;
  retryLabel: string;
}

function ErrorPanel({ onRetry, retryLabel }: ErrorPanelProps) {
  return (
    <Card className="mx-auto my-10 max-w-xl">
      <CardHeader title={TITLE} />
      <CardBody className="space-y-4">
        {/* `role="alert"` rather than a toast: this replaced the content, so it has to be
            announced as the thing that is now here (rules §48.5). */}
        <p role="alert" className="text-base text-ink">
          {BODY}
        </p>
        <Button onClick={onRetry}>{retryLabel}</Button>
      </CardBody>
    </Card>
  );
}

/** What the router renders when a route component throws (`defaultErrorComponent`). */
export function RouteErrorFallback({ reset }: { reset?: () => void }) {
  return (
    <ErrorPanel
      retryLabel="Try again"
      onRetry={() => {
        reset?.();
      }}
    />
  );
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  failed: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* A no-op unless `VITE_SENTRY_DSN` is set, and scrubbed by `beforeSend` either way. */
    Sentry.captureException(error, {
      // The stack of components, which names files rather than data.
      contexts: { react: { componentStack: info.componentStack } },
      tags: { boundary: 'app-root' },
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <ErrorPanel
        retryLabel="Reload"
        onRetry={() => {
          /* The tree above the router is gone, so there is nothing local to re-render:
             a reload is the honest retry here. */
          window.location.reload();
        }}
      />
    );
  }
}
